import type { ReactElement } from 'react';
import { Button } from '@/shared/ui';
import type {
  CylinderPreviewControl,
  CylinderPreviewMaskStatus,
} from '../model/use-cylinder-preview';

interface CylinderPreviewPanelProps {
  readonly control: CylinderPreviewControl;
}

/** 켜진 미리보기가 실루엣대로 잘리지 않는 이유(결정 R2·D-K). 'ready' 면 안내가 없다. */
const MASK_NOTICES: Readonly<Record<CylinderPreviewMaskStatus, string | null>> = {
  none: '마스크를 올리면 실루엣대로 잘립니다.',
  expired: '마스크 표시 URL 이 만료되었습니다. ‘서버 상태·사진 URL 확인’을 눌러 주세요.',
  ready: null,
};

/**
 * 저장하지 않는 원통 미리보기를 켜고 끄는 패널(결정 Q4). 원통 상품에서만 보인다 — 부모가 실측 종류로 가른다.
 * 입력 칸은 없다. 미리보기는 초안의 원통 표면(사각형·곡률)과 합성 설정(마스크·multiply)을 그대로 쓴다.
 */
export const CylinderPreviewPanel = ({ control }: CylinderPreviewPanelProps): ReactElement => {
  const maskNotice = control.enabled ? MASK_NOTICES[control.maskStatus] : null;
  return (
    <section aria-label="원통 미리보기" className="space-y-3 rounded-lg border p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold">원통 미리보기</h2>
        <span className="rounded-full border border-amber-500 px-2 py-0.5 text-xs font-medium text-amber-700">
          저장 안 됨
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        표면 설정의 사각형·곡률로 체크무늬를 원통에 감아 사진 위에 겹쳐 봅니다. 미리보기는
        편집영역에 저장되지 않습니다.
      </p>
      {control.available ? null : (
        <p className="text-sm text-muted-foreground">
          면에 원통 표면이 있고 이 색상의 면 사진이 있어야 미리보기를 켤 수 있습니다.
        </p>
      )}
      <Button
        type="button"
        variant="outline"
        aria-pressed={control.enabled}
        disabled={!control.available}
        onClick={control.toggle}
      >
        {control.enabled ? '미리보기 끄기' : '미리보기 켜기'}
      </Button>
      {maskNotice === null ? null : <p className="text-sm text-muted-foreground">{maskNotice}</p>}
      {control.error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {control.error}
        </p>
      )}
      {control.renderMs === null ? null : (
        <p className="text-xs text-muted-foreground">
          생성 {Math.round(control.renderMs)}ms (허용 시간 100ms)
        </p>
      )}
    </section>
  );
};
